// ** Redux Imports
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { createDedupedThunk } from '../../../../../../../utility/createDedupedThunk'
import * as transfersApi from '../api'
import { DEPARTMENTS } from '../constants'
import { getIntermediary } from '../../../../register/management/intermediaries/store'

//Una llave por lectura: cada lista lleva su propio estado en listsMeta para que la petición
//de una no marque como cargando (ni descarte la respuesta de) otra
const LIST_KEYS = [
    'activitiesPending',
    'activitiesPendingUSD',
    'allBankActivities',
    'allBankActivitiesUSD',
    'myActivities',
    'myActivitiesUSD',
    'myActivitiesTraditional',
    'myActivitiesAppliedTraditional',
    'myActivitiesApplied',
    'myActivitiesAppliedUSD',
    'activitiesTaken',
    'activitiesTakenUSD',
    'activitiesIntermediary',
    'activitiesToTransfer'
]

const initialListMeta = { status: 'idle', error: null, requestId: null }

const asList = (data) => (Array.isArray(data) ? data : [])
const isLatest = (meta, action) => meta?.requestId === action.meta.requestId

//Texto que createAsyncThunk pone en action.error.message al rechazar con rejectWithValue: no viene del backend
const RTK_REJECTED_SENTINEL = 'Rejected'

//`error` guarda lo que dijo el backend y nada más. Un fallo sin respuesta (caída de red) no dice nada, así que
//se guarda null en vez del literal en inglés de Redux Toolkit: quien lo muestre pondrá su propio texto. Que la
//petición falló ya lo dice `status`
const readError = (action) => {
    const message = action.error?.message

    return action.payload ?? (message === RTK_REJECTED_SENTINEL ? null : message) ?? null
}

//Llave del índice de actividades asignadas: el modal de estadísticas las pide por usuario y moneda
const assignedKey = ({ id, currency } = {}) => `${id}:${currency || 'MXN'}`

/**
 * Crea un thunk de lectura sin caché: cada dispatch sale a la red.
 *
 * createDedupedThunk sólo corta un dispatch con los mismos argumentos mientras el primero
 * sigue en vuelo; una búsqueda con otros filtros siempre sale y la respuesta que llegue
 * tarde se descarta por requestId. Un dispatch cortado resuelve rechazado con
 * `meta.condition: true` y sin `payload`: quien lea el resultado del dispatch debe ignorarlo.
 */
const createReadThunk = (type, fetcher) => createDedupedThunk(type, async (arg, { rejectWithValue }) => {
    try {
        return {
            data: await fetcher(arg)
        }
    } catch (error) {
        return rejectWithValue(error.response?.data)
    }
})

/**
 * Crea un thunk de descarga: el payload es el blob del archivo y no se guarda en el store.
 *
 * Empieza con `get`, así que si falla el toast lo muestra el middleware; el componente sólo
 * debe evitar armar el archivo con el payload del error.
 */
const createDownloadThunk = (type, fetcher) => createAsyncThunk(type, async (_, { rejectWithValue }) => {
    try {
        return await fetcher()
    } catch (error) {
        return rejectWithValue(error.response?.data)
    }
})

/**
 * Registra pending/fulfilled/rejected de una lectura sobre `listsMeta[key]`.
 *
 * El requestId descarta las respuestas que llegan tarde (por ejemplo al filtrar dos veces
 * seguidas), que si no sobreescribirían la tabla con datos de la consulta anterior.
 * `normalize` aplica a las listas que la pantalla recorre con map/filter.
 */
const addListCases = (builder, thunk, key, { normalize = false } = {}) => {
    builder.addCase(thunk.pending, (state, action) => {
        state.listsMeta[key] = { ...state.listsMeta[key], status: 'pending', error: null, requestId: action.meta.requestId }
    })
    builder.addCase(thunk.fulfilled, (state, action) => {
        if (!isLatest(state.listsMeta[key], action)) return
        state[key] = normalize ? asList(action.payload.data) : action.payload.data
        state.listsMeta[key] = { ...state.listsMeta[key], status: 'fulfilled' }
    })
    builder.addCase(thunk.rejected, (state, action) => {
        if (!isLatest(state.listsMeta[key], action)) return
        state.listsMeta[key] = {
            ...state.listsMeta[key],
            status: 'rejected',
            error: readError(action)
        }
    })
}

//**TODO: handleChecked {Table Activities Banks and register} */
export const getAllBankActivities = createReadThunk('appBankActivities/getAllBankActivities', (rangeDates) => transfersApi.fetchAllBankActivities('MXN', rangeDates))
export const getAllBankActivitiesUSD = createReadThunk('appBankActivities/getAllBankActivitiesUSD', (rangeDates) => transfersApi.fetchAllBankActivities('USD', rangeDates))

//**TODO: handleChecked {table register intermediaries} */
export const getBankActivitiesIntermediary = createReadThunk('appBankActivities/getBankActivitiesIntermediary', transfersApi.fetchBankActivitiesIntermediary)

// Case to Transfer
export const getBankActivitiesToTransfer = createReadThunk('appBankActivities/getBankActivitiesToTransfer', () => transfersApi.fetchBankActivitiesToTransfer())

export const requestTransferBankActivityIntermediary = createAsyncThunk('appBankActivities/requestTransferBankActivityIntermediary', async (bankActivity, { dispatch, rejectWithValue }) => {
    try {
        const data = await transfersApi.requestTransferBankActivity(bankActivity)
        //No se hace `unwrap`: si la recarga falla, la escritura sigue siendo un éxito
        await dispatch(getBankActivitiesIntermediary({ id: bankActivity?.intermediary_id }))
        return data
    } catch (error) {
        return rejectWithValue(error.response?.data)
    }
})

export const transferBankActivityIntermediary = createAsyncThunk('appBankActivities/transferBankActivityIntermediary', async (bankActivity, { dispatch, rejectWithValue }) => {
    try {
        const data = await transfersApi.transferBankActivityIntermediary(bankActivity)
        await dispatch(getBankActivitiesIntermediary({ id: bankActivity?.currentIntermediary_id }))
        await dispatch(getIntermediary(bankActivity?.currentIntermediary_id))
        return data
    } catch (error) {
        return rejectWithValue(error.response?.data)
    }
})

export const authorizeTransferBankActivityIntermediary = createAsyncThunk('appBankActivities/authorizeTransferBankActivityIntermediary', async (bankActivity, { dispatch, rejectWithValue }) => {
    try {
        const data = await transfersApi.authorizeTransferBankActivity(bankActivity)
        await dispatch(getBankActivitiesToTransfer())
        return data
    } catch (error) {
        return rejectWithValue(error.response?.data)
    }
})

//**TODO: handleChecked {Card activities assigned } ✅ */
//prefix Taken
//Se guarda por usuario y moneda en activitiesAssigned; se lee con selectActivitiesAssigned y su estado con
//selectActivitiesAssignedMeta
export const getAllBankActivitiesAssigned = createReadThunk('appBankActivities/getAllBankActivitiesAssigned', transfersApi.fetchBankActivitiesAssigned)

export const selectActivitiesAssigned = (state, user) => state.bankActivities.activitiesAssigned[assignedKey(user)]

//Se exporta para que nadie fuera de la store tenga que armar la llave: { status, error, requestId }, o undefined
//si ese usuario y moneda nunca se han pedido
export const selectActivitiesAssignedMeta = (state, user) => state.bankActivities.activitiesAssignedMeta[assignedKey(user)]

//**TODO: checked {table assign activities} ✅*/
export const getBankActivitiesPending = createReadThunk('appBankActivities/getBankActivitiesPending', (rangeDates) => transfersApi.fetchBankActivitiesPending('MXN', rangeDates))

//**TODO: checked {table assign activities} ✅*/
export const getBankActivitiesPendingUSD = createReadThunk('appBankActivities/getBankActivitiesPendingUSD', (rangeDates) => transfersApi.fetchBankActivitiesPending('USD', rangeDates))

//**TODO: checked {table get my activities} ✅*/
// Return All Bank Activities assigned to me from MXN and All Departments
export const getMyBankActivities = createReadThunk('appBankActivities/getMyBankActivities', ({ rangeDates }) => transfersApi.fetchMyBankActivities({ status: 'taken', currency: 'MXN', rangeDates }))

//**TODO: checked {table get my activities} ✅*/
// Return All Bank Activities assigned to me from MXN and All Departments
export const getMyBankActivitiesUSD = createReadThunk('appBankActivities/getMyBankActivitiesUSD', ({ rangeDates }) => transfersApi.fetchMyBankActivities({ status: 'taken', currency: 'USD', rangeDates }))

export const getMyBankActivitiesTraditional = createReadThunk('appBankActivities/getMyBankActivitiesTraditional', ({ rangeDates }) => transfersApi.fetchMyBankActivities({ status: 'taken', currency: 'MXN', department: DEPARTMENTS.TOLUCA, rangeDates }))

//**TODO: checked {table get my activities applied} ✅*/
//Return Activities Applied from MXN and allDepartments
export const getMyBankActivitiesApplied = createReadThunk('appBankActivities/getMyBankActivitiesApplied', ({ rangeDates }) => transfersApi.fetchMyBankActivities({ status: 'applied', currency: 'MXN', rangeDates }))

//**TODO: checked {table get my activities applied} ✅*/
//Return Activities Applied from MXN and allDepartments
export const getMyBankActivitiesAppliedUSD = createReadThunk('appBankActivities/getMyBankActivitiesAppliedUSD', ({ rangeDates }) => transfersApi.fetchMyBankActivities({ status: 'applied', currency: 'USD', rangeDates }))

export const getMyBankActivitiesAppliedTraditional = createReadThunk('appBankActivities/getMyBankActivitiesAppliedTraditional', ({ rangeDates }) => transfersApi.fetchMyBankActivities({ status: 'applied', currency: 'MXN', department: DEPARTMENTS.TOLUCA, rangeDates }))

//**TODO: checked {card activities taken, applied & total #Activities}  ✅*/
export const getActivitiesTaken = createReadThunk('appBankActivities/getActivitiesTaken', (rangeDates) => transfersApi.fetchActivitiesTaken('MXN', rangeDates))

//**TODO: checked {card activities taken, applied & total #Activities}  ✅*/
export const getActivitiesTakenUSD = createReadThunk('appBankActivities/getActivitiesTakenUSD', (rangeDates) => transfersApi.fetchActivitiesTaken('USD', rangeDates))

export const getActivitiesLayout = createDownloadThunk('appBankActivities/getActivitiesLayout', transfersApi.fetchActivitiesLayout)

export const addLayoutSipare = createAsyncThunk('appBankActivities/addLayoutSipare', async (layout, { rejectWithValue }) => {
    try {
        return await transfersApi.uploadLayoutSipare(layout)
    } catch (error) {
        return rejectWithValue(error.response?.data)
    }
})

export const createMultipleActivities = createAsyncThunk('appBankActivities/createMultipleActivities', async (data, { rejectWithValue }) => {
    try {
        return await transfersApi.createMultipleActivities(data)
    } catch (error) {
        return rejectWithValue(error.response?.data)
    }
})

//**TODO: checked {add activity from movements} ✅*/
export const addBankActivity = createAsyncThunk('appBankActivities/addBankActivity', async (bankActivity, { dispatch, rejectWithValue }) => {
    try {
        const data = await transfersApi.createBankActivity(bankActivity)
        await dispatch(getAllBankActivities())
        return data
    } catch (error) {
        return rejectWithValue(error.response?.data)
    }
})

//**TODO: checked {Update activity from register and movements} ✅*/
export const updateBankActivity = createAsyncThunk('appBankActivities/updateBankActivity', async (bankActivity, { dispatch, rejectWithValue }) => {
    try {
        const data = await transfersApi.updateBankActivity(parseInt(bankActivity.get("id")), bankActivity)
        await dispatch(getAllBankActivities())
        return data
    } catch (error) {
        return rejectWithValue(error.response?.data)
    }
})

//Asignar, transferir y desasignar sólo escriben: las listas las recarga quien llama, que sabe
//por la ruta si la pantalla es MXN o USD
//**TODO: checked {Assign activity from department ✅}*/
export const assignActivity = createAsyncThunk('appBankActivities/assignActivity', async (data, { rejectWithValue }) => {
    try {
        return await transfersApi.assignActivity(data)
    } catch (error) {
        return rejectWithValue(error.response?.data)
    }
})

//**TODO: checked {Transfer activity from department} ✅*/
export const transferActivity = createAsyncThunk('appBankActivities/transferActivity', async (data, { rejectWithValue }) => {
    try {
        return await transfersApi.transferActivity(data)
    } catch (error) {
        return rejectWithValue(error.response?.data)
    }
})

//**TODO: checked {unAssign activity table activities BANKS} ✅*/
export const dropActivities = createAsyncThunk('appBankActivities/dropActivities', async (data, { rejectWithValue }) => {
    try {
        return await transfersApi.dropActivities(data)
    } catch (error) {
        return rejectWithValue(error.response?.data)
    }
})

//**TODO: checked ✅*/
export const getLayoutTransfers = createDownloadThunk('appBankActivities/getLayoutTransfers', transfersApi.fetchLayoutTransfers)
//**TODO: checked ✅*/
export const uploadLayoutTransfers = createAsyncThunk('appBankActivities/uploadLayoutTransfers', async (layout, { rejectWithValue }) => {
    try {
        return await transfersApi.uploadLayoutTransfers(layout)
    } catch (error) {
        return rejectWithValue(error.response?.data)
    }
})

//**TODO: checked {delete activity from register and movements} ✅ */
export const cancelBankActivity = createAsyncThunk('appBankActivities/cancelBankActivity', async (bankActivityId, { dispatch, rejectWithValue }) => {
    try {
        const data = await transfersApi.cancelBankActivity(bankActivityId)
        await dispatch(getAllBankActivities())
        return data
    } catch (error) {
        return rejectWithValue(error.response?.data)
    }
})

//**TODO: checked {complete activity assigned to me} ✅*/
export const applyBankActivity = createAsyncThunk('appBankActivities/applyBankActivity', async (bankActivity, { rejectWithValue }) => {
    try {
        return await transfersApi.applyBankActivity(parseInt(bankActivity.get("id")), bankActivity)
    } catch (error) {
        return rejectWithValue(error.response?.data)
    }
})

//Edita una actividad ya aplicada desde Mis transferencias: cambiarle la cuenta o reemplazarle el voucher
//**TODO: checked {change account from myActivities} ✅ */
export const editAppliedActivity = createAsyncThunk('appBankActivities/editAppliedActivity', async (bankActivity, { rejectWithValue }) => {
    try {
        return await transfersApi.editAppliedActivity(parseInt(bankActivity.get("id")), bankActivity)
    } catch (error) {
        return rejectWithValue(error.response?.data)
    }
})

export const appBankActivitiesSlice = createSlice({
    name: 'appBankActivities',
    initialState: {
        activitiesPending: [],
        activitiesPendingUSD: [],
        allBankActivities: [],
        allBankActivitiesUSD: [],
        myActivities: [],
        myActivitiesUSD: [],
        myActivitiesTraditional: [],
        myActivitiesAppliedTraditional: [],
        myActivitiesApplied: [],
        myActivitiesAppliedUSD: [],
        activitiesTaken: [],
        activitiesTakenUSD: [],
        activitiesIntermediary: [],
        activitiesToTransfer: [],
        //Índice por usuario y moneda: si se guardara en una sola lista, al abrir el modal de
        //otro usuario se verían las del anterior mientras carga o si la petición falla
        activitiesAssigned: {},
        //Estado de cada llave de activitiesAssigned: { [llave]: { status, error, requestId } }
        activitiesAssignedMeta: {},
        //Estado de cada lista: { [llave]: { status, error, requestId } }
        listsMeta: Object.fromEntries(LIST_KEYS.map(key => [key, { ...initialListMeta }]))
    },
    reducers: {},
    extraReducers: builder => {
        addListCases(builder, getMyBankActivitiesTraditional, 'myActivitiesTraditional', { normalize: true })
        addListCases(builder, getMyBankActivitiesUSD, 'myActivitiesUSD', { normalize: true })
        addListCases(builder, getAllBankActivities, 'allBankActivities', { normalize: true })
        addListCases(builder, getAllBankActivitiesUSD, 'allBankActivitiesUSD', { normalize: true })
        addListCases(builder, getBankActivitiesPending, 'activitiesPending', { normalize: true })
        addListCases(builder, getBankActivitiesPendingUSD, 'activitiesPendingUSD', { normalize: true })
        addListCases(builder, getMyBankActivities, 'myActivities', { normalize: true })
        addListCases(builder, getMyBankActivitiesApplied, 'myActivitiesApplied', { normalize: true })
        addListCases(builder, getMyBankActivitiesAppliedUSD, 'myActivitiesAppliedUSD', { normalize: true })
        addListCases(builder, getMyBankActivitiesAppliedTraditional, 'myActivitiesAppliedTraditional', { normalize: true })
        //activitiesTaken es un objeto ({ taken, applied, ... }), no una lista: no se normaliza
        addListCases(builder, getActivitiesTaken, 'activitiesTaken')
        addListCases(builder, getActivitiesTakenUSD, 'activitiesTakenUSD')
        addListCases(builder, getBankActivitiesIntermediary, 'activitiesIntermediary', { normalize: true })
        addListCases(builder, getBankActivitiesToTransfer, 'activitiesToTransfer', { normalize: true })

        builder.addCase(getAllBankActivitiesAssigned.pending, (state, action) => {
            const key = assignedKey(action.meta.arg)
            state.activitiesAssignedMeta[key] = { ...state.activitiesAssignedMeta[key], status: 'pending', error: null, requestId: action.meta.requestId }
        })
        builder.addCase(getAllBankActivitiesAssigned.fulfilled, (state, action) => {
            const key = assignedKey(action.meta.arg)
            if (!isLatest(state.activitiesAssignedMeta[key], action)) return
            state.activitiesAssigned[key] = asList(action.payload.data)
            state.activitiesAssignedMeta[key] = { ...state.activitiesAssignedMeta[key], status: 'fulfilled' }
        })
        builder.addCase(getAllBankActivitiesAssigned.rejected, (state, action) => {
            const key = assignedKey(action.meta.arg)
            if (!isLatest(state.activitiesAssignedMeta[key], action)) return
            state.activitiesAssignedMeta[key] = {
                ...state.activitiesAssignedMeta[key],
                status: 'rejected',
                error: readError(action)
            }
        })
    }
}
)

export default appBankActivitiesSlice.reducer
